<template>
  <div class="smart-display">
    <h1>{{ msg }}</h1>

    <p>
    空間知能化のお勉強をしましょう。
    </p>

<h2>
JavaScriptの同期処理
</h2>

<h4>
もくじ
</h4>

<div class="talka">
<ol>
<li>
<a href="#1">async/awaitを使用</a>
</li>
<li>
<a href="#2">Promiseを使用</a>
</li>
</ol>

</div>

<h3 id="1">
1.async/awaitを使用
</h3>

<div class="talka">

<h4>
asyncとは
</h4>

<ul>
<li>
非同期関数を定義する関数宣言のことです。
</li>
<li>
以下のように関数の前にasyncを宣言することにより、非同期関数（async function）を定義できます。
</li>
</ul>

<pre>
async function sample() {}
</pre>

<h4>
awaitとは
</h4>

<ul>
<li>
async function内でPromiseの結果（resolve、reject）が返されるまで待機する（処理を一時停止する）演算子のこと。
</li>
<li>
以下のように、関数の前にawaitを指定すると、その関数のPromiseの結果が返されるまで待機する。
</li>
</ul>

<pre>
async function sample() {
    const result = await sampleResolve();

    // sampleResolve()のPromiseの結果が返ってくるまで以下は実行されない
    console.log(result);
}
</pre>

<h4>
async/awaitを使用したアプリの例
</h4>

<ul>
<li>
非同期処理関数の頭に「async」を付ける。
</li>
<li>
非同期処理関数を呼び出す場合は、関数の頭に「await」を付ける。
</li>
</ul>

<pre>
//setTimeoutをいちいち書くのが面倒なので関数化
function Timeout(passVal, ms) {
   return new Promise(resolve =>
      setTimeout(() => {
          resolve(passVal);
      }, ms)
   )
}
// 非同期処理：f1()
async function f1(){
    console.log("#1: f1");
    return "f1 ==> f2";
}
// 非同期処理：f2()
async function f2(passVal){
    console.log("#2: f2");
    return Timeout("f2 ==> f3", Math.random()*2000);
}
// 非同期処理：f3()
async function f3(passVal){
    console.log("#3: f3");
    return Timeout("f3 ==> f4", Math.random()*3000);
}
// 非同期処理：f4()
async function f4(passVal){
     console.log("#4: f4");
     return Timeout("f4", Math.random()*3000);
}
// 非同期処理：f1()～f4()を順番に呼び出す
async function runAll(){
    try{
        console.log("[START]");
        // 非同期処理：f1()を呼び出す
        const res1 = await f1();
        console.log(res1);
        // 非同期処理：f2()を呼び出す
        const res2 = await f2(res1);
        console.log(res2);
        // 非同期処理：f3()を呼び出す
        const res3 = await f3(res2);
        console.log(res3);
        // 非同期処理：f4()を呼び出す
        const res4 = await f4(res3);
        console.log("Final function: " + res4);

        console.log("[END]");

    } catch(err) {
        //エラー処理
        //とりあえず何もしない
    }
}

runAll();
</pre>



</div>

<h3 id="2">
2.Promiseを使用
</h3>

<div class="talka">

<h4>
Promiseインターフェイスとは
</h4>

<ul>
<li>
Promise インターフェイスは作成時点では分からなくてもよい値へのプロキシです。
</li>
<li>
Promise を用いることで、非同期アクションが最終的に成功した時の値や失敗した時の理由に対するハンドラーを関連付けることができます。
</li>
<li>
これにより、非同期メソッドは、最終的な値をすぐに返す代わりに、未来のある時点で値を持つ Promise を返すことで、同期メソッドと同じように値を返すことができるようになります。
</li>
<li>
Promise の状態は以下のいずれかとなります。
</li>
<ol>
<li>
pending: 初期状態。成功も失敗もしていません。
</li>
<li>
fulfilled: 処理が成功して完了したことを意味します。
</li>
<li>
rejected: 処理が失敗したことを意味します。
</li>
</ol>
<li>
pending 状態の Promise は、何らかの値を持つ fulfilled 状態、もしくは何らかの理由 (エラー) を持つ rejected 状態のいずれかに変わります。
</li>
<li>
そのどちらとなっても、then メソッドによって関連付けられたハンドラーが呼ばれます。
<br>(対応するハンドラーがアタッチされたとき、既に Promise が成功または失敗していても、そのハンドラーは呼ばれます。
<br>よって、非同期処理とその関連付けられたハンドラーとのレースコンディションは発生しません。)
</li>
<li>
Promise.prototype.then() メソッドと Promise.prototype.catch() メソッドもまた Promise を返すので、これらをチェーン (連鎖) させることができます。
</li>

</ul>

<figure style="max-width: 800px;">
<img src="http://www.hiroict.com/img/akiko/promises.png" width="100%">
<figcaption align="center">Promiseチェーン</figcaption>
</figure>

<h4>
Promiseを使用したアプリの例
</h4>

<pre>
function f1(){
    return new Promise((resolve,reject) => {
        console.log("[START]");
        console.log("#1: f1");
        resolve("f1 ==> f2");
    })
}
function f2(passVal){
    return new Promise((resolve,reject) => {
    //setTimeoutは動きをそれっぽくするために入れているだけなので削除可
    setTimeout(
        () => {
            //f1のresolve内の "f1 ==> f2" がpassValに代入される
            console.log(passVal);
            console.log("#2: f2");
            resolve("f2 ==> f3");
         }
         ,Math.random()*2000)
     })
}

function f3(passVal){
    return new Promise((resolve,reject) => {
    setTimeout(
        () => {
            console.log(passVal);            
            console.log("#3: f3");
            resolve("f3 ==> f4");
        }
         ,Math.random()*3000)
     })
}

function f4(passVal){
    return new Promise((resolve,reject) => {
    setTimeout(
        () => {
            console.log(passVal);
            console.log("#4: f4");
            resolve("f4");
        }
          ,Math.random()*4000)
    })
}



//f1->f2->f3->f4の順に実行
f1()
.then(f2)
.then(f3)
.then(f4)
.then((response)=> {
    console.log("Final function: "+response);
    console.log("[END]");
}
)
</pre>



</div>





  </div>

</template>

<script>
import Vue from 'vue'
import VueYoutube from 'vue-youtube'

Vue.use(VueYoutube)

export default {
  name: 'DanceWorld',
  props: {
    msg: String
  },
  data() {
    return {
      //album_index: 0,
      //album_title : 'ワルツ',
      //video_index: 0,
      //video_title : '01 いきいきストレッチング',

      //video_index_1: 0,
      //video_id: '5IlfzLYadNk',

      //albums: [],
    }
  },
  created () {
    this.runAll();
    //this.albums = [this.album_1, this.album_2, this.album_3, this.album_4, this.album_5, this.album_6, this.album_7]
    //this.firstVideo()
  },
  methods: {
    async runAll(){
      try{
        console.log("[START]");
        const res1 = await this.f1();
        console.log(res1);

        const res2 = await this.f2(res1);
        console.log(res2);

        const res3 = await this.f3(res2);
        console.log(res3);

        const res4 = await this.f4(res3);
        console.log("Final function: " + res4);

        console.log("[END]");

      }catch(err){
        //エラー処理
        //とりあえず何もしない
      }
    },
    //setTimeoutをいちいち書くのが面倒なので関数化
    Timeout(passVal, ms) {
      return new Promise(resolve =>
        setTimeout(() => {
            resolve(passVal);
        }, ms)
      )
    },
    async f1(){
      console.log("#1: f1");
      return "f1 ==> f2";
    },
    async f2(passVal){
      console.log("#2: f2 passVal: " + passVal);
      return this.Timeout("f2 ==> f3", Math.random()*2000);
    },
    async f3(passVal){
      console.log("#3: f3 passVal: " + passVal);
      return this.Timeout("f3 ==> f4", Math.random()*3000);
    },
    async f4(passVal){
        console.log("#4: f4 passVal: " + passVal);
        return this.Timeout("f4", Math.random()*3000);
    },
    /*
    selectAlbum(index) {
      window.console.log("selectAlbum() 1.index: " + index);
      this.album_index = index;
      this.firstVideo()
    },
    */
  }
  
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.smart-display {
  //margin-left: 20px;
}

.talka {
  margin-left: 10px;
}

pre {
  margin-left: 10px;
  background-color: #ffffaa;
}

h3 {
  margin: 40px 0 0;
}
ol {
  //list-style-type: none;
  padding: 0;
  margin-left: 20px;
}
ul {
  //list-style-type: none;
  padding: 0;
  margin-left: 20px;
}
li {
  //display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
</style>
